/**
 * Persisted transport visual offset.
 *
 * Live's transport reaches the PWA via: LOM observer (~15 Hz) → UDP → bridge
 * → WebSocket → render. The cumulative latency is ~80–150 ms depending on
 * machine, browser, and network path. The result is the PWA's readout
 * trailing Live's audio by a perceptible amount.
 *
 * The offset is a per-device knob (stored in localStorage) the user drags
 * to bias the readout forward in time. We DO NOT push this back to the
 * bridge or to Max — it's purely a visual correction at the consumer end,
 * so a laptop preview and an iPad on stage can each have their own value.
 *
 * The offset is in MILLISECONDS. Positive = show ahead of incoming data
 * (compensates for downstream lag). Negative = show behind (rarely useful).
 */

const KEY = 'opbuttons.transport.offset_ms';
const DEFAULT_OFFSET = 0;
const MIN_OFFSET = -500;
const MAX_OFFSET = 500;

export function loadOffset(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_OFFSET;
  const raw = localStorage.getItem(KEY);
  if (raw === null) return DEFAULT_OFFSET;
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_OFFSET;
  return clampOffset(n);
}

export function saveOffset(ms: number): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(KEY, String(clampOffset(ms)));
}

export function clampOffset(ms: number): number {
  return Math.max(MIN_OFFSET, Math.min(MAX_OFFSET, Math.round(ms)));
}

export const OFFSET_RANGE = { min: MIN_OFFSET, max: MAX_OFFSET } as const;
